// Account.js

import React, { useState, useEffect, useContext } from 'react';
import {
  Button, Input, Flex, VStack, Text, Box, Divider, useToast, Switch, HStack,
} from '@chakra-ui/react';
import UserSettingsLayout from './UserSettingsLayout';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { UserContext } from './UserContext';
import AuthMethods from './auth/AuthMethods';
import { authPost } from './auth/socialAuth';
import { authDestination } from './client/authContinuation';
import { Link as RouterLink } from 'react-router-dom';

const Account = () => {
  const { userDetails, setUserDetails } = useContext(UserContext);
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [emailUnsubscribed, setEmailUnsubscribed] = useState(false);
  const [prefLoading, setPrefLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [reauthenticate, setReauthenticate] = useState(false);
  const toast = useToast(); // Initialize the useToast hook

  useEffect(() => {
    if (userDetails?.email) {
      setUserEmail(userDetails.email);
    }
    fetchWithAuth('/api/user/email-preferences')
      .then(r => r.json())
      .then(d => setEmailUnsubscribed(d.email_unsubscribed ?? false))
      .catch(() => {});
  }, [userDetails]);

  const handleEmailPrefToggle = async () => {
    const newVal = !emailUnsubscribed;
    setPrefLoading(true);
    try {
      await fetchWithAuth('/api/user/email-preferences', {
        method: 'PATCH',
        body: JSON.stringify({ email_unsubscribed: newVal }),
      });
      setEmailUnsubscribed(newVal);
      toast({
        title: newVal ? 'Emails turned off' : 'Emails turned on',
        description: newVal
          ? 'You will no longer receive check-in emails from Pabs.'
          : 'You will receive check-in emails from Pabs again.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({ title: 'Failed to update preference', status: 'error', duration: 3000 });
    } finally {
      setPrefLoading(false);
    }
  };

  const handleEmailUpdate = async () => {
    if (!email && !pendingEmail) return;
    setEmailBusy(true);
    try {
      if (!pendingEmail) {
        await authPost('email-change/start', { email });
        setPendingEmail(email);
        toast({ title: 'Check your new email for a code.', status: 'success', duration: 4000 });
      } else {
        const data = await authPost('email-change/confirm', { code: emailCode });
        localStorage.setItem('token', data.token);
        window.dispatchEvent(new Event('storage'));
        setUserEmail(data.email); setUserDetails({ ...userDetails, email: data.email });
        setEmail(''); setPendingEmail(''); setEmailCode('');
        toast({ title: 'Email updated.', status: 'success', duration: 4000 });
      }
    } catch (error) {
      setReauthenticate(error.status === 401);
      toast({ title: error.message, status: 'error', duration: 5000, isClosable: true });
    } finally { setEmailBusy(false); }
  };

  const sendPasswordSetup = async () => {
    setEmailBusy(true);
    try {
      const response = await fetch('/api/request-reset', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: userDetails.email }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      toast({ title: 'Check your email for a password setup link.', status: 'success', duration: 5000 });
    } catch (error) { toast({ title: error.message || 'Could not send a link. Try again.', status: 'error', duration: 5000 }); }
    finally { setEmailBusy(false); }
  };

  const handlePasswordUpdate = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast({
        title: 'All fields are required',
        description: 'Please fill out all password fields.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Make sure both password fields match.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/user/update-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'Password updated',
          description: 'Your password has been updated successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        toast({
          title: 'Failed to update password',
          description: data.error,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error updating password:', error);
      toast({
        title: 'Error updating password',
        description: 'An error occurred while updating your password.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <UserSettingsLayout>
      <Box width="100%" bg="white" px={{ base: 5, md: 10 }} py={8}>
        <Box mb={8} maxW="720px">
          <Text fontSize="xl" fontWeight="700" color="brand.700">Account Settings</Text>
          <Text fontSize="sm" color="gray.900" mt={1}>Manage your email address and password.</Text>
        </Box>
        <VStack align="flex-start" width="100%" maxW="720px">

          {/* Email Update Section */}
          <Box width="100%" mb={10}>
            <Text fontSize="lg" fontWeight="bold" mb={2}>
              Email Address
            </Text>
            <Flex>
              <Text fontSize="sm" fontWeight="normal" mb={2} mr={2}>
                Your email address is
              </Text>
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                {userEmail || '[Loading...]'}
              </Text>
            </Flex>

            <VStack spacing={4} align="stretch" width={{ base: '100%', md: '50%' }}>
              <Input
                isDisabled={Boolean(pendingEmail)}
                aria-label="New email address"
                type="email"
                placeholder="New Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="filled"
                bg="gray.50"
              />
              {pendingEmail && <>
                <Text fontSize="sm">Enter the code sent to {pendingEmail}. Your current email stays unchanged until you confirm.</Text>
                <Input aria-label="Email verification code" placeholder="6-digit code" value={emailCode} onChange={e => setEmailCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" />
                <Button variant="link" alignSelf="flex-start" onClick={() => { setPendingEmail(''); setEmailCode(''); }}>Use a different email</Button>
              </>}
              <Button
                isLoading={emailBusy}
                colorScheme="blue"
                onClick={handleEmailUpdate}
                alignSelf="flex-start"
                width="auto"
              >
                {pendingEmail ? 'Confirm email' : 'Update Email'}
              </Button>
              {reauthenticate && <Button as={RouterLink} to={authDestination('/login','/account')} variant="link">Sign in again to continue</Button>}
            </VStack>
          </Box>

          <Divider />

          {/* Password Update Section */}
          <Box width="100%" pt={10}>
            <Text fontSize="lg" fontWeight="bold" mb={2}>
              Password
            </Text>
            {userDetails?.has_password === false ? <>
              <Text fontSize="sm" mb={4}>You sign in with a connected provider. You can also add a password using a link sent to your email.</Text>
              <Button onClick={sendPasswordSetup} isLoading={emailBusy}>Email me a password setup link</Button>
            </> : <VStack spacing={4} align="stretch" width={{ base: '100%', md: '50%' }}>
              <Input
                placeholder="Your Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                variant="filled"
                bg="gray.50"
              />
              <Input
                placeholder="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                variant="filled"
                bg="gray.50"
              />
              <Input
                placeholder="Confirm New Password"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                variant="filled"
                bg="gray.50"
              />
              <Button
                colorScheme="blue"
                onClick={handlePasswordUpdate}
                alignSelf="flex-start"
                width="auto"
              >
                Update Password
              </Button>
            </VStack>}
          </Box>

          <Divider />

          <AuthMethods />
          <Divider />

          {/* Email Preferences Section */}
          <Box width="100%" pt={10}>
            <Text fontSize="lg" fontWeight="bold" mb={1}>Email Preferences</Text>
            <Text fontSize="sm" color="gray.500" mb={6}>
              Control whether I reach out to you by email.
            </Text>
            <HStack justify="space-between" align="flex-start" maxW="480px"
              p={4} borderRadius="lg" border="1px solid"
              borderColor={emailUnsubscribed ? 'gray.200' : 'brand.100'}
              bg={emailUnsubscribed ? 'gray.50' : 'brand.50'}>
              <Box>
                <Text fontSize="sm" fontWeight="600" color="gray.800">
                  {emailUnsubscribed ? 'I\'m staying quiet' : 'I\'m keeping an eye on things'}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={0.5}>
                  {emailUnsubscribed
                    ? 'You won\'t hear from me in your inbox. Flip it back on whenever.'
                    : 'I\'ll drop you a note when I notice something in your plan worth your attention.'}
                </Text>
              </Box>
              <Switch
                isChecked={!emailUnsubscribed}
                onChange={handleEmailPrefToggle}
                isDisabled={prefLoading}
                colorScheme="brand"
                size="md"
                mt={0.5}
              />
            </HStack>
          </Box>

        </VStack>
      </Box>
    </UserSettingsLayout>
  );
};

export default Account;
